// The design tokens and type, bundled so they do not cost a second render-
// blocking request. The file stays in public/ because the server-rendered share
// page still links it. It goes first, where its <link> used to sit in index.html.
import '../public/colors_and_type.css';
// The type and spacing tokens (docs/type-system.md), before anything that names them.
import './ui/type.css';
// The UI kit loads next so screen stylesheets can override its materials.
import './ui/kit.css';
import './ui/world.css';
import './ui/pressState';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { initSentry } from './lib/sentry';
import { initTheme } from './lib/theme';
import { initHyphenation } from './lib/hyphenation';
import { WorldStage } from './world/WorldStage';
import { applyReadingFont } from './world/prefs';
import { installChunkReload } from './lib/chunkReload';
import { initBrowserVoice } from './lib/browserVoice';

installChunkReload();
// The browser voice (lib/browserVoice.ts): asks the server whether it serves
// one and, on a capable device, loads it in the background, after the first
// screen has had the network to itself.
window.setTimeout(() => { void initBrowserVoice(); }, 2500);
initTheme();
// Before first paint: the stylesheet justifies the slip opinion only when this
// says the browser can hyphenate, so measuring late would flash the variant
// this exists to avoid. See lib/hyphenation.ts.
initHyphenation();
applyReadingFont();
document.documentElement.classList.add('world-on');
void initSentry();

const root = document.getElementById('root');
if (!root) throw new Error('missing #root');
async function start(el: HTMLElement) {
  // Dev-only renderer harness, outside auth so it runs without a backend.
  if (import.meta.env.DEV && window.location.pathname === '/__world') {
    const { default: WorldLab } = await import('./world/dev/WorldLab');
    ReactDOM.createRoot(el).render(<WorldStage><WorldLab /></WorldStage>);
    return;
  }
  ReactDOM.createRoot(el).render(
    <React.StrictMode>
      <BrowserRouter>
        <WorldStage>
          <ToastProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ToastProvider>
        </WorldStage>
      </BrowserRouter>
    </React.StrictMode>,
  );
}
void start(root);
